import { forwardRef, useEffect, useRef, useState } from 'react';
import HTMLFlipBook from 'react-pageflip';
import * as pdfjsLib from 'pdfjs-dist';

// Worker — must match the installed pdfjs-dist version (4.10.38)
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs';

// A single page wrapper — react-pageflip requires forwardRef children
const Page = forwardRef(function Page({ src, label, index, total }, ref) {
  return (
    <div ref={ref} className="bg-bg shadow-2xl select-none">
      <div className="relative w-full h-full">
        {src ? (
          <img
            src={src}
            alt={label || `Page ${index + 1}`}
            className="block w-full h-full object-contain bg-bg"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-sub text-xs tracking-[0.3em] uppercase">
            Loading…
          </div>
        )}
        <div className="absolute bottom-3 left-0 right-0 flex items-center justify-between px-4 text-[10px] tracking-[0.3em] uppercase text-sub/70 pointer-events-none">
          <span>Pod Bristol</span>
          <span>{String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
        </div>
      </div>
    </div>
  );
});

// Cover page (different styling so it reads like a book cover)
const Cover = forwardRef(function Cover({ children }, ref) {
  return (
    <div ref={ref} className="bg-ink text-bg flex flex-col justify-between p-8 md:p-10">
      {children}
    </div>
  );
});

export default function Flipbook({ pdfUrl, title = 'Pod Bristol', kicker = 'Drawing Package', subtitle = 'Floor plans · floor finish schedules · construction details · pod specifications.' }) {
  const [pages, setPages] = useState([]); // array of dataURLs
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState({ w: 500, h: 707 });
  const [pageIndex, setPageIndex] = useState(0);
  const [aspect, setAspect] = useState(0.707); // width/height of a PDF page
  const flipRef = useRef(null);
  const containerRef = useRef(null);

  // Load PDF and rasterise pages to dataURLs
  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const task = pdfjsLib.getDocument({
          url: pdfUrl,
          withCredentials: false,
          cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/cmaps/',
          cMapPacked: true,
          standardFontDataUrl:
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/standard_fonts/',
        });
        const pdf = await task.promise;
        const imgs = [];
        let firstAspect = aspect;
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1 });
          if (i === 1) {
            firstAspect = viewport.width / viewport.height;
          }
          // Render at a sensible resolution (~700px wide)
          const targetWidth = 700;
          const scale = targetWidth / viewport.width;
          const v = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          canvas.width = v.width;
          canvas.height = v.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport: v }).promise;
          imgs.push(canvas.toDataURL('image/jpeg', 0.75));
          // Free memory
          canvas.width = 0;
          canvas.height = 0;
          page.cleanup();
          if (cancelled) return;
        }
        if (!cancelled) {
          setAspect(firstAspect);
          setPages(imgs);
          setLoading(false);
        }
      } catch (e) {
        console.error('Flipbook PDF load failed:', e);
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Responsive sizing based on container width (single-page width)
  useEffect(() => {
    const onResize = () => {
      const el = containerRef.current;
      if (!el) return;
      // book opens to two pages — wrapper width fits both. Use full container width.
      const containerW = el.clientWidth;
      const isDouble = window.innerWidth >= 900;
      const pageW = isDouble ? Math.floor(containerW / 2) : containerW;
      const pageH = Math.floor(pageW / aspect);
      setSize({ w: pageW, h: pageH });
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [aspect, pages.length]);

  const total = pages.length;
  const goPrev = () => flipRef.current?.pageFlip()?.flipPrev();
  const goNext = () => flipRef.current?.pageFlip()?.flipNext();

  return (
    <div ref={containerRef} data-testid="flipbook-wrap" className="w-full">
      {loading && (
        <div className="aspect-[1.4/1] w-full flex items-center justify-center bg-surface rounded-sm border border-line">
          <div className="flex flex-col items-center gap-3 text-sub">
            <div className="h-6 w-6 border-2 border-ink/20 border-t-ink rounded-full animate-spin" />
            <span className="text-[10px] tracking-[0.3em] uppercase">Preparing flipbook…</span>
          </div>
        </div>
      )}

      {!loading && pages.length > 0 && (
        <>
          <div className="relative" style={{ height: size.h }}>
            <HTMLFlipBook
              ref={flipRef}
              width={size.w}
              height={size.h}
              size="fixed"
              minWidth={200}
              minHeight={283}
              maxWidth={1200}
              maxHeight={1700}
              showCover={true}
              flippingTime={800}
              usePortrait={size.w * 2 > (containerRef.current?.clientWidth || 0) + 1}
              mobileScrollSupport={true}
              maxShadowOpacity={0.5}
              drawShadow={true}
              className=""
              style={{}}
              startPage={0}
              clickEventForward={true}
              useMouseEvents={true}
              swipeDistance={30}
              showPageCorners={true}
              disableFlipByClick={false}
              onFlip={(e) => setPageIndex(e.data)}
            >
              {pages.map((src, i) => (
                <Page key={i} src={src} index={i} total={total} />
              ))}
            </HTMLFlipBook>
          </div>

          {/* Controls */}
          <div className="mt-6 flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={goPrev}
              data-testid="flipbook-prev"
              className="btn-ghost"
              aria-label="Previous page"
            >
              <span aria-hidden>←</span> Prev
            </button>
            <div className="text-[10px] tracking-[0.32em] uppercase text-sub tabular">
              {String(Math.min(pageIndex + 1, total)).padStart(2, '0')} / {String(total).padStart(2, '0')}
            </div>
            <button
              type="button"
              onClick={goNext}
              data-testid="flipbook-next"
              className="btn-ghost"
              aria-label="Next page"
            >
              Next <span aria-hidden>→</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
